import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export async function fetchWithDelay<T>(
  items: T[],
  fetchFn: (item: T) => Promise<any>,
  delayMs = 250
) {
  const results = [];
  for (const item of items) {
    results.push(await fetchFn(item));
    await new Promise(res => setTimeout(res, delayMs));
  }
  return results;
}

/**
 * Defensive render-layer coercion. Some Oracle (Agent C) narrative fields are
 * typed as `string` but can arrive from the model as a structured object
 * (e.g. `{ title, description }`). Rendering such an object directly as a React
 * child throws React error #31 ("Objects are not valid as a React child").
 * This flattens any non-string value into a safe display string. Mirrors the
 * server-side `sanitizeString` behavior so already-persisted memos render too.
 * Plain strings (incl. markdown) pass through unchanged.
 */
export function coerceToText(val: unknown): string {
  if (val == null) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (Array.isArray(val)) {
    return val.map((item) => coerceToText(item)).filter(Boolean).join(" ");
  }
  if (typeof val === "object") {
    const entries = Object.entries(val as Record<string, unknown>)
      .filter(([, v]) => typeof v === "string" && v.length > 0)
      .map(([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1)}: ${v}`);
    return entries.join(" — ");
  }
  return String(val);
}
